import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { CommonTitle } from '../../../components/items/common.title';
import { Button } from '../../../components/items/Button';
import { useForm } from 'react-hook-form';
import { OnOffButton } from '../../../components/items/on.off.button';
import { useEffect, useState } from 'react';
import { fetchStandardTask, updateStandardTask } from '../../../api/mypage';
import { TextArea } from '../../../components/items/joinInput';
import { AlertModal } from '../../../components/modal/alert.modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { companyStandardTaskSchema } from '../../../schema/mypageSchema';

interface StandardTaskState {
  halfDay: string;
  fullDay: string;
  etcCondition: string;
  otCondition: string;
  cycleCondition: string;
  fuelSupport: boolean;
}

const DefaultCostSetting = () => {
  const [switchStates, setSwitchStates] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적
  const history = useHistory();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<StandardTaskState>({
    resolver: zodResolver(companyStandardTaskSchema),
    defaultValues: {
      fuelSupport: false, // 기본값 설정
    },
  });

  console.log(switchStates);

  // 화면 로딩 시 데이터를 가져와 폼 필드에 설정
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchStandardTask();
        // 가져온 데이터가 "0"이면 빈 문자열로 설정하여 placeholder를 보여줍니다.
        setValue('halfDay', data.halfDay === '0' ? '' : data.halfDay);
        setValue('fullDay', data.fullDay === '0' ? '' : data.fullDay);
        setValue(
          'etcCondition',
          data.etcCondition === '0' ? '' : data.etcCondition,
        );
        setValue(
          'otCondition',
          data.otCondition === '0' ? '' : data.otCondition,
        );
        setValue(
          'cycleCondition',
          data.cycleCondition === '0' ? '' : data.cycleCondition,
        );
        setSwitchStates(data.fuelSupport); // 유류비 지원 값 설정
        setValue('fuelSupport', data.fuelSupport); // fuelSupport 값을 setValue로 설정
      } catch (error) {
        console.error('데이터를 가져오는 중 오류가 발생했습니다:', error);
      }
    };

    fetchData();
  }, [setValue]);

  const onSubmit = async (data: StandardTaskState) => {
    try {
      const response = await updateStandardTask({
        ...data,
        fuelSupport: getValues('fuelSupport'),
      });

      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('작업 기준 업데이트에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error('업데이트 오류:', error);
      setModalMessage('작업 기준 업데이트 중 오류가 발생했습니다.');
      setIsModalOpen(true);
      setNavigateOnClose(false);
    }
  };

  const toggleSwitch = () => {
    setSwitchStates((prevState) => !prevState);
    setValue('fuelSupport', !switchStates); // toggleSwitch 함수에서 직접 setValue로 업데이트
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/mypage',
      }); // navigateOnClose가 true일 때만 /mypage로 이동
    }
  };

  return (
    <Container>
      <CommonTitle title="작업 기준 변경" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="setting-wrap">
          <div className="box-item">
            <TextArea
              name="fullDay"
              label="한나절 조건"
              placeholder="금액,시간, km, 회전수,기름 등을 자세히 기입하여 조건을 서술해 주세요."
              register={register}
              errors={
                errors.fullDay?.message
                  ? [errors.fullDay.message as string]
                  : []
              }
            />
          </div>
          <div className="box-item">
            <TextArea
              name="halfDay"
              label="반일 조건"
              placeholder="금액,시간, km, 회전수,기름 등을 자세히 기입하여 조건을 서술해 주세요."
              register={register}
              errors={
                errors.halfDay?.message
                  ? [errors.halfDay.message as string]
                  : []
              }
            />
          </div>
          <div className="box-item">
            <TextArea
              name="etcCondition"
              label="기타협업 조건"
              placeholder="금액,시간, km, 회전수,기름 등을 자세히 기입하여 조건을 서술해 주세요."
              register={register}
              errors={
                errors.etcCondition?.message
                  ? [errors.etcCondition.message as string]
                  : []
              }
            />
          </div>
          <div className="box-item">
            <TextArea
              name="otCondition"
              label="O.T 조건"
              placeholder="금액,시간, km, 회전수,기름 등을 자세히 기입하여 조건을 서술해 주세요."
              register={register}
              errors={
                errors.otCondition?.message
                  ? [errors.otCondition.message as string]
                  : []
              }
            />
          </div>
          <div className="box-item">
            <TextArea
              name="cycleCondition"
              label="회전(탕) 조건"
              placeholder="금액,시간, km, 회전수,기름 등을 자세히 기입하여 조건을 서술해 주세요."
              register={register}
              errors={
                errors.cycleCondition?.message
                  ? [errors.cycleCondition.message as string]
                  : []
              }
            />
          </div>
          <div className="box-item on-off-btn">
            <div className="title">유류비 지원</div>
            <OnOffButton
              isOn={switchStates}
              toggleSwitch={() => toggleSwitch()}
            />
          </div>
        </div>
        <Button title="저 장" type="submit" fiexd />
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default DefaultCostSetting;

// style
const Container = styled.div`
  padding: 2.4rem 2rem;
  height: 100%;
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    height: 100%;
    padding-bottom: 3rem;
    overflow: scroll;
    max-height: calc(100% - 15rem);
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera*/
    }
    .setting-wrap {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
      height: calc(100% - 16rem);
      .title {
        font-size: 1.5rem;
        font-weight: 600;
        display: flex;
        align-items: flex-start;
        margin-bottom: 0.8rem;
        span {
          color: #ff5151;
          margin-left: 0.2rem;
          transform: translateY(-3px);
        }
      }
      .input-box {
        margin-bottom: 0.7rem;
        input {
          width: 100%;
          border: 1px solid #d6dbe1;
          padding: 1.4rem 1.8rem 1.5rem;
          border-radius: 0.8rem;
        }
      }
      .text-box {
        flex: 1;
        border-radius: 0.8rem;
        border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
        overflow: hidden;
        padding: 1.3rem 1.8rem 1rem;
        font-size: 1.4rem;
        textarea {
          width: 100%;
          height: 100%;
          flex: 1;
          min-height: 3.6rem;
          padding: 0.1rem;
          border: none;
          font-weight: 400;
          &::placeholder {
            color: ${(props) => props.theme.searchPlaceholderColor};
            font-size: 1.4rem;
            font-weight: 00;
          }
        }
      }
      .on-off-btn {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .title {
          margin-bottom: 0;
        }
      }
    }
  }
`;
